import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Eye, EyeOff, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { toApiError } from '@/api/errors'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { fetchBranding } from '@/features/auth/api'
import { BrandPanel } from '@/features/auth/components/BrandPanel'
import { DemoAccounts } from '@/features/auth/components/DemoAccounts'
import { PortalSwitcher } from '@/features/auth/components/PortalSwitcher'
import { useGuardians } from '@/features/auth/guardians'
import { portalMeta } from '@/features/auth/portals'
import { loginSchema, type LoginForm } from '@/features/auth/schemas'
import { portalHome, type Portal } from '@/features/auth/types'
import { useAuth } from '@/features/auth/useAuth'
import { LanguagePicker } from '@/features/preferences/components/PreferenceControls'

export function LoginPage() {
  const { t } = useTranslation('auth')
  const [params, setParams] = useSearchParams()
  const guardians = useGuardians()
  const portal = portalMeta(params.get('portal')).key
  const meta = portalMeta(portal, guardians)
  const next = params.get('next')
  const navigate = useNavigate()
  const { login, status, portal: activePortal } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  // The error is remembered together with the portal it belongs to, so switching portal clears it.
  const [failure, setFailure] = useState<{ portal: Portal; message: string } | null>(null)
  const formError = failure?.portal === portal ? failure.message : null
  const branding = useQuery({ queryKey: ['branding'], queryFn: fetchBranding, staleTime: 5 * 60_000 })

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', remember: true },
  })

  const pageTitle = t('Sign in · {{portal}}', { portal: t(meta.label) })
  useEffect(() => {
    document.title = pageTitle
  }, [pageTitle])

  if (status === 'authenticated' && activePortal === portal)
    return <Navigate to={next ?? portalHome[portal]} replace />

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null)
    try {
      const session = await login({ ...values, portal })
      void navigate(next && next.startsWith(portalHome[session.portal]) ? next : portalHome[session.portal], {
        replace: true,
      })
    } catch (e) {
      setFailure({ portal, message: toApiError(e).message })
    }
  })

  const switchPortal = (p: Portal) => setParams({ portal: p }, { replace: true })
  const Icon = meta.icon
  // Lower-case English phrases kept as keys so the English button reads exactly as before.
  const target = {
    admin: t('administration'),
    staff: t('staff & teachers'),
    family: guardians ? t('the family portal') : t('the student portal'),
  }[portal]

  return (
    <div className="grid min-h-dvh grid-cols-1 bg-surface lg:grid-cols-[1.08fr_1fr]">
      <BrandPanel school={branding.data?.school ?? null} portal={portal} />

      <main className="relative flex min-w-0 items-center justify-center px-5 py-10 sm:px-8">
        <div
          aria-hidden="true"
          className="bg-canvas-glow pointer-events-none absolute inset-x-0 top-0 h-64 lg:hidden"
        />
        <div className="absolute top-5 right-5 z-10 w-28 max-w-[7rem]">
          <LanguagePicker compact />
        </div>
        <div className="relative w-full max-w-[26rem] min-w-0">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="bg-brand-gradient grid size-10 place-items-center rounded-xl text-white shadow-brand">
              <Icon className="size-5" aria-hidden="true" />
            </span>
            <p className="min-w-0 text-sm font-semibold break-words">
              {branding.data?.school?.name ?? 'SchoolEduca'}
            </p>
          </div>

          <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-100">
            <Icon className="size-3.5" aria-hidden="true" />
            {t(meta.label)}
          </span>
          <h2 className="mt-4 text-[1.75rem] leading-tight font-semibold break-words">{t('Welcome back')}</h2>
          <p className="mt-1 text-sm text-muted">
            {t('{{description}} — sign in to continue.', { description: t(meta.description) })}
          </p>

          <div className="mt-6">
            <PortalSwitcher value={portal} onChange={switchPortal} />
          </div>

          <form
            onSubmit={onSubmit}
            noValidate
            className="mt-6 space-y-4"
            aria-describedby={formError ? 'login-error' : undefined}
          >
            {formError ? (
              <div id="login-error">
                <Alert tone="danger">{formError}</Alert>
              </div>
            ) : null}

            <FormField label={t('Email address')} error={errors.email?.message} required>
              <Input
                type="email"
                autoComplete="username"
                placeholder={t('you@school.org')}
                autoFocus
                size="lg"
                leading={<Mail />}
                {...register('email')}
              />
            </FormField>

            <FormField label={t('Password')} error={errors.password?.message} required>
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                size="lg"
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="grid size-8 place-items-center rounded-xs text-subtle hover:text-ink"
                    aria-label={showPassword ? t('Hide password') : t('Show password')}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                }
                {...register('password')}
              />
            </FormField>

            <div className="flex items-center justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <Controller
                  name="remember"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="remember"
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(v === true)}
                    />
                  )}
                />
                <label htmlFor="remember" className="cursor-pointer text-sm text-ink-soft">
                  {t('Keep me signed in')}
                </label>
              </div>
            </div>

            <Button
              type="submit"
              variant="gradient"
              size="lg"
              block
              loading={isSubmitting}
              rightIcon={<ArrowRight />}
              // Long portal names ("espace personnel et enseignants") wrap instead of widening the page.
              className="h-auto min-h-11 py-2.5 text-center leading-snug whitespace-normal"
            >
              {t('Sign in to {{portal}}', { portal: target })}
            </Button>
          </form>

          {import.meta.env.DEV ? (
            <DemoAccounts
              portal={portal}
              onPick={(email, password) => {
                setValue('email', email, { shouldValidate: true })
                setValue('password', password, { shouldValidate: true })
              }}
            />
          ) : null}

          <p className="mt-10 text-center text-xs text-subtle">
            © {new Date().getFullYear()} {branding.data?.school?.name ?? 'SchoolEduca'}
          </p>
        </div>
      </main>
    </div>
  )
}
